// InventoryModal.js
import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import axiosInstance from '../axiosConfig';
import debounce from 'lodash.debounce';
import './InventoryModal.css';
import './EntityModals.css';

function InventoryModal({ show, handleClose, componentId, actionType, serviceCenterId, onUpdateComponent }) {
    const [quantity, setQuantity] = useState(0);
    const [error, setError] = useState(null);
    const [actionTitle, setActionTitle] = useState('');
    const [reason, setReason] = useState(''); // Причина возврата
    const [defectSource, setDefectSource] = useState(''); // Источник брака
    const [returnedByQuery, setReturnedByQuery] = useState(''); // Текст запроса
    const [returnedByOptions, setReturnedByOptions] = useState([]); // Предложения пользователей
    const [returnedById, setReturnedById] = useState(''); // ID выбранного пользователя
    const [defectFiles, setDefectFiles] = useState([]); // Файлы для дефектов
    const [inActFiles, setInActFiles] = useState([]); // Файлы для оприходования
    const [defectPhotosUrls, setDefectPhotosUrls] = useState([]); // URL загруженных фото дефектов
    const [inActPhotosUrls, setInActPhotosUrls] = useState([]); // URL загруженных актов оприходования
    const [userFullName, setUserFullName] = useState(''); // ФИО пользователя

    // Функция для поиска пользователей с дебаунсом
    const searchUsers = debounce((query) => {
        if (query.length < 2) {
            setReturnedByOptions([]);
            return;
        }
        axiosInstance.get(`/api/users/search/?query=${encodeURIComponent(query)}`)
            .then(response => {
                setReturnedByOptions(response.data.users);
            })
            .catch(err => {
                console.error(err);
                setError('Не удалось загрузить список пользователей.');
            });
    }, 300); // Задержка 300ms

    useEffect(() => {
        switch (actionType) {
            case 'IN':
                setActionTitle('Оприходовано');
                break;
            case 'OUT':
                setActionTitle('Выдано');
                break;
            case 'RETURN':
                setActionTitle('Возвращено');
                break;
            default:
                setActionTitle('Inventory Action');
        }

        // Сбросить состояния при открытии модального окна
        if (show) {
            setQuantity(0);
            setError(null);
            setReason('');
            setDefectSource('');
            setReturnedByQuery('');
            setReturnedByOptions([]);
            setReturnedById('');
            setDefectFiles([]);
            setInActFiles([]);
            setDefectPhotosUrls([]);
            setInActPhotosUrls([]);
            setUserFullName('');
        }
    }, [actionType, show]);

    const handleFileChange = (e, type) => {
        if (type === 'defect') {
            setDefectFiles([...e.target.files]); // Устанавливаем массив файлов из input
        } else if (type === 'in_act') {
            setInActFiles([...e.target.files]); // Устанавливаем массив файлов из input
        }
    };

    const handleReasonChange = (e) => {
        setReason(e.target.value);
        // Если причина изменена, сбрасываем источник брака и файлы
        if (e.target.value !== 'defect') {
            setDefectSource('');
            setDefectFiles([]);
        }
    };

    const handleDefectSourceChange = (e) => {
        setDefectSource(e.target.value);
    };

    const handleReturnedByChange = (e) => {
        const query = e.target.value;
        setReturnedByQuery(query);
        setReturnedById('');
        searchUsers(query);
    };

    const handleSelectUser = (user) => {
        setReturnedByQuery(`${user.first_name} ${user.last_name}`);
        setReturnedById(user.id);
        setReturnedByOptions([]);
    };

    const handleSave = () => {
        setError(null);

        // Валидация
        if (actionType === 'RETURN') {
            if (!reason) {
                setError('Пожалуйста, выберите причину возврата.');
                return;
            }

            if (!returnedById) {
                setError('Пожалуйста, выберите пользователя, совершившего возврат.');
                return;
            }

            if (reason === 'defect') {
                if (!defectSource) {
                    setError('Пожалуйста, выберите источник брака.');
                    return;
                }
                if (defectSource === 'from_warehouse' && defectFiles.length === 0) {
                    setError('Пожалуйста, загрузите хотя бы один файл акта дефекта.');
                    return;
                }
                // Quantity is handled based on defectSource in the backend
            } else if (reason === 'simple_return') {
                if (quantity <= 0) {
                    setError('Количество должно быть больше нуля для простого возврата.');
                    return;
                }
            }
        } else if (actionType === 'IN') {
            if (inActFiles.length === 0) {
                setError('Пожалуйста, загрузите хотя бы один файл акта оприходования.');
                return;
            }
            if (quantity <= 0) {
                setError('Количество должно быть больше нуля для оприходования.');
                return;
            }
        } else {
            if (quantity <= 0) {
                setError('Количество должно быть больше нуля.');
                return;
            }
        }

        const formData = new FormData();
        formData.append('component_id', componentId);
        formData.append('action_type', actionType);

        if (actionType === 'RETURN') {
            formData.append('reason', reason);
            formData.append('returned_by', returnedById); // Передаём ID пользователя

            if (reason === 'defect') {
                formData.append('defect_source', defectSource); // Добавляем источник брака
                defectFiles.forEach(file => {
                    formData.append('receipt_files', file); // Добавляем каждый файл в FormData
                });
                formData.append('quantity', defectSource === 'from_warehouse' ? 1 : 0); // Списываем только если из склада
            } else if (reason === 'simple_return') {
                formData.append('quantity', quantity);
            }
        } else if (actionType === 'IN') {
            inActFiles.forEach(file => {
                formData.append('in_act_photos', file); // Используем ключ 'in_act_photos' для акта оприходования
            });
            formData.append('quantity', quantity);
        } else {
            formData.append('quantity', quantity);
            // Если требуется файл для других действий, добавьте здесь
        }

        axiosInstance.post('/api/inventory-action/', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
        .then(response => {
            if (response.data.success) {
                console.log('Action completed successfully', response.data);

                // Обновляем данные компонента
                onUpdateComponent();

                // Устанавливаем ссылки на акты поступления или дефектов, если есть
                if (response.data.defect_photos_urls && response.data.defect_photos_urls.length > 0) {
                    setDefectPhotosUrls(response.data.defect_photos_urls);
                }
                if (response.data.in_photos_urls && response.data.in_photos_urls.length > 0) {
                    setInActPhotosUrls(response.data.in_photos_urls);
                }

                // Устанавливаем ФИО пользователя
                if (response.data.user_full_name) {
                    setUserFullName(response.data.user_full_name);
                }

                // Показать уведомление об успешной операции
                window.Toastify({
                    text: `${actionTitle} успешно!`,
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "linear-gradient(to right, #00b09b, #96c93d)",
                    stopOnFocus: true,
                    className: 'custom-toast',
                }).showToast();

                // Закрываем модальное окно
                handleClose();
            } else {
                console.error('Unexpected response:', response);
                setError('Произошла ошибка при выполнении операции. Пожалуйста, попробуйте еще раз.');
            }
        })
        .catch(error => {
            console.error('Request failed:', error);
            if (error.response && error.response.data) {
                setError(
                    error.response.data.message ||
                    error.response.data.detail ||
                    'Произошла ошибка при выполнении операции. Пожалуйста, попробуйте еще раз.'
                );
            } else {
                setError('Произошла ошибка при выполнении операции. Пожалуйста, попробуйте еще раз.');
            }
        });
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            className="entity-modal"
            dialogClassName="entity-modal-dialog entity-modal-dialog--sm"
            contentClassName="entity-modal-content"
            centered
        >
            <Modal.Header closeButton>
                <Modal.Title>{actionTitle}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <Alert variant="danger">{error}</Alert>}
                <Form>
                    {actionType !== 'RETURN' && (
                        <>
                            {actionType === 'IN' && (
                                <Form.Group controlId="inActFiles">
                                    <Form.Label>Файлы акта оприходования (можно загрузить несколько)</Form.Label>
                                    <Form.Control
                                        type="file"
                                        onChange={(e) => handleFileChange(e, 'in_act')}
                                        accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        multiple
                                        required
                                    />
                                </Form.Group>
                            )}
                            <Form.Group controlId="quantity">
                                <Form.Label>Количество</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={quantity}
                                    onChange={(e) => setQuantity(parseInt(e.target.value, 10))}
                                    min="1"
                                    required
                                />
                            </Form.Group>
                        </>
                    )}

                    {actionType === 'RETURN' && (
                        <>
                            <Form.Group controlId="returnedByUser">
                                <Form.Label>Возврат совершил</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={returnedByQuery}
                                    onChange={handleReturnedByChange}
                                    placeholder="Введите имя и фамилию пользователя"
                                    autoComplete="off"
                                    required
                                />
                                {returnedByOptions.length > 0 && (
                                    <div className="autocomplete-dropdown">
                                        {returnedByOptions.map(user => (
                                            <div
                                                key={user.id}
                                                className="autocomplete-option"
                                                onClick={() => handleSelectUser(user)}
                                            >
                                                {user.first_name} {user.last_name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </Form.Group>

                            <Form.Group controlId="returnReason">
                                <Form.Label>Причина возврата</Form.Label>
                                <Form.Control as="select" value={reason} onChange={handleReasonChange} required>
                                    <option value="">-- Выберите причину --</option>
                                    <option value="defect">Брак</option>
                                    <option value="simple_return">Простой возврат</option>
                                </Form.Control>
                            </Form.Group>

                            {reason === 'defect' && (
                                <>
                                    <Form.Group controlId="defectSource">
                                        <Form.Label>Источник брака</Form.Label>
                                        <Form.Control
                                            as="select"
                                            value={defectSource}
                                            onChange={handleDefectSourceChange}
                                            required
                                        >
                                            <option value="">-- Выберите источник брака --</option>
                                            <option value="from_engineer">Возврат от инженера</option>
                                            <option value="from_warehouse">Брак со склада</option>
                                        </Form.Control>
                                    </Form.Group>

                                    {/* Изменённая метка для загрузки файлов дефекта */}
                                    <Form.Group controlId="receiptFiles">
                                        <Form.Label>
                                            {defectSource === 'from_warehouse' ? 
                                                'Файлы акта дефекта (можно загрузить несколько)' :
                                                'Файлы акта дефекта (опционально)'}
                                        </Form.Label>
                                        <Form.Control
                                            type="file"
                                            onChange={(e) => handleFileChange(e, 'defect')}
                                            accept="image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                            multiple
                                            required={defectSource === 'from_warehouse'}
                                        />
                                        {defectSource === 'from_engineer' && (
                                            <Form.Text className="text-muted">
                                                Загрузите файлы по необходимости.
                                            </Form.Text>
                                        )}
                                    </Form.Group>
                                </>
                            )}

                            {reason === 'simple_return' && (
                                <Form.Group controlId="quantity">
                                    <Form.Label>Количество</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={quantity}
                                        onChange={(e) => setQuantity(parseInt(e.target.value, 10))}
                                        min="1"
                                        required
                                    />
                                </Form.Group>
                            )}
                        </>
                    )}
                </Form>
                {(defectPhotosUrls.length > 0 || inActPhotosUrls.length > 0) && (
                    <div className="alert alert-success mt-3">
                        {defectPhotosUrls.map((url, index) => (
                            <div key={index}>
                                <a href={url} target="_blank" rel="noopener noreferrer">Скачать акт дефекта #{index + 1}</a>
                            </div>
                        ))}
                        {inActPhotosUrls.map((url, index) => (
                            <div key={`in_act-${index}`}>
                                <a href={url} target="_blank" rel="noopener noreferrer">Скачать акт оприходования #{index + 1}</a>
                            </div>
                        ))}
                    </div>
                )}
                {userFullName && (
                    <div className="alert alert-info mt-3">
                        <strong>Возврат выполнен пользователем:</strong> {userFullName}
                    </div>
                )}
                {returnedById && (
                    <div className="alert alert-info mt-3">
                        <strong>Возврат совершил:</strong> {returnedByQuery}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Отмена
                </Button>
                <Button variant="primary" onClick={handleSave}>
                    Сохранить
                </Button>
            </Modal.Footer>
            
        </Modal>
    );

}

export default InventoryModal;
